/**
 * **D345 — the soft floor: whether this container's build is old enough that an update should
 * interrupt the resident rather than only mark the menu.**
 *
 * The operator sets `prompt_build_number` per release, on the dial, because urgency is a property
 * of the release and only the operator knows it, only once it ships. Under the default `menu`
 * answer (`pwa/updateAlerts.ts`) a routine update raises the dot and nothing else; one whose build
 * is below this number raises D285's prompt on Home as well.
 *
 * **Three `false` returns, each one the quiet answer, and every one of them is D330's.** The PWA
 * and a sideload carry no `storeBuild`; a document with no soft floor names none; and a cold cache
 * runs the compiled-in defaults, which carry none. D345 (f): the soft floor fails *quiet*, because
 * a dial that fails in the loud direction recreates the complaint it answers on exactly the
 * devices the operator cannot reach.
 *
 * **It is a separate question from `isBuildBelowFloor` and deliberately not a second return of
 * it.** The hard floor is read once, before any provider, and blocks the app; this is read on
 * every render of the shell, subscribed, and decides only whether a prompt that the rail and the
 * resident's preference already allow is raised. A cached soft floor persisting offline is
 * harmless here for the reason D330's is costly there: the prompt still needs the store to be
 * offering the update (`useAppUpdate`'s `phase === 'available'`), so it can only ever offer an
 * update that exists.
 *
 * **Pushed into the prompt from `App.tsx`, never pulled by `useAppUpdate`.** `screens/home/`
 * imports that hook, so a `pwa/` → `config/` edge would put the open path one hop from this
 * directory — the import-graph walk in `infra/tests/client-config-client.test.ts` reddens on it.
 */

import { useSyncExternalStore } from 'react'
import { BUILD } from '../pwa/version'
import { getClientConfig, subscribeToClientConfig } from './store'

export function isBuildBelowPromptFloor(): boolean {
  const build = BUILD.storeBuild
  if (build === undefined) return false
  const floor = getClientConfig().promptBuildNumber
  if (floor === undefined) return false
  return build < floor
}

/** The same read, subscribed — a document that lands after launch can raise the prompt. */
export function useBuildBelowPromptFloor(): boolean {
  return useSyncExternalStore(subscribeToClientConfig, isBuildBelowPromptFloor, isBuildBelowPromptFloor)
}
